/**
 * Canais FAST: as colunas da aba "Canais FAST" da planilha de atas, na mesma ordem, com os mesmos títulos e larguras
 * (a primeira coluna não tem título na planilha). A mesma lista monta a tela, a exportação para Excel e o link externo.
 */
import type { DeliveryStatus } from './domain.js';

export const FAST_COLUMNS = [
  { key: 'mark', title: '', label: 'Marca (coluna sem título)', width: 3.29 },
  { key: 'clientLabel', title: 'Cliente', label: 'Cliente', width: 20.29 },
  { key: 'contentPartner', title: 'Content Partner', label: 'Content Partner', width: 20 },
  { key: 'kind', title: 'canal/ endpoint', label: 'canal/ endpoint', width: 19.43 },
  { key: 'channelName', title: 'Nome do Canal', label: 'Nome do Canal', width: 37.43 },
  { key: 'platform', title: 'Plataforma', label: 'Plataforma', width: 15.29 },
  { key: 'region', title: 'Região', label: 'Região', width: 11.57 },
  { key: 'deliveryFormat', title: 'Delivery', label: 'Delivery', width: 20.71 },
  { key: 'codec', title: 'CODEC', label: 'CODEC', width: 11.86 },
  { key: 'launch', title: 'Lançamento', label: 'Lançamento', width: 16.29 },
  { key: 'forecastDate', title: 'Previsão', label: 'Previsão', width: 13 },
  { key: 'statusText', title: 'Status', label: 'Status', width: 46.71 },
  { key: 'progressPct', title: 'Progresso', label: 'Progresso', width: 21.86 },
  { key: 'opNumber', title: 'OP', label: 'OP', width: 6.29 },
  { key: 'description', title: 'Descrição', label: 'Descrição', width: 63.71 },
  { key: 'tapRef', title: 'TAP', label: 'TAP', width: 6.71 },
] as const;
export type FastColumnKey = (typeof FAST_COLUMNS)[number]['key'];
export const FAST_COLUMN_KEYS = FAST_COLUMNS.map((c) => c.key) as [FastColumnKey, ...FastColumnKey[]];

/** Colunas de uso interno, fora do link externo por padrão (quem gera o link pode incluir). */
export const FAST_INTERNAL_COLUMNS: FastColumnKey[] = ['mark', 'opNumber', 'description', 'tapRef'];

export const FAST_KIND_LABEL = { canal: 'Canal', endpoint: 'EndPoint' } as const;

/** Sugestões dos campos de texto livre: os valores que já aparecem na planilha (dá para digitar outro). */
export const FAST_SUGGESTIONS: Partial<Record<FastColumnKey, string[]>> = {
  platform: ['Samsung TV+', 'LG', 'TCL', 'ROKU', 'Só Play', 'Claro', 'Pluto', 'Globoplay', 'You Tube', 'Ditu', '+SBT', 'Disney', 'Mercado Play', 'NOS',
    'Newco Play', 'Xiaomi', 'MULTTV', 'Rakuten', 'Future Today'],
  region: ['BR', 'LATAM', 'USA', 'Portugal'],
  deliveryFormat: ['HLS', 'SRT/ Entitlement', 'HLS3 - HLS4 - DASH', 'MSS', 'SRT'],
  codec: ['AVC/H264', 'HEVC/H265'],
  statusText: ['Lançado', 'Cancelado', 'ON HOLD', 'Finalizado por email', 'Aguardando request Connect'],
};

const plain = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Categoria do canal (contagens, cores e a aba Entregas do projeto), lida do texto livre da coluna Status, do texto da
 * coluna Lançamento ("Cancelado"), do progresso e da data de lançamento. O texto da planilha nunca é alterado.
 */
export function fastCategory(r: { statusText?: string | null; launchText?: string | null; launchDate?: string | null; progressPct?: number | null }, today: string): DeliveryStatus {
  const t = plain(`${r.statusText ?? ''} ${r.launchText ?? ''}`);
  if (/cancel|candel/.test(t)) return 'cancelado';
  if (/on ?hold|pausad|suspens/.test(t)) return 'pausado';
  if (/lancad|finalizad|no ar\b/.test(t)) return 'lancado';
  if (/entreg|delivery|feed|aguard|andamento|config|teste|homolog/.test(t)) return 'em_andamento';
  if (r.progressPct != null && r.progressPct >= 100) return 'lancado';
  if (r.progressPct != null && r.progressPct > 0) return 'em_andamento';
  if (r.launchDate) return r.launchDate <= today ? 'lancado' : 'previsto';
  return 'previsto';
}
